// ORDER-TYPE-EXCEPTION-CRUD
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { Modal } from "src/components";
import Button from "src/features/fields/components/Button";
import { useToast } from "src/hooks/useToast";
import GenericForm from "src/pages/counter-master/form.component";
import { getOrderCapFreezeCounters } from "src/services/counter-api";
import { queryDivisionDetails } from "src/services/division-api";
import { getOrderTypeExceptionValidation } from "src/services/oms-api";
import { addOrderTypeException } from "src/services/order-api";
import { usePoHandlers } from "../PoController";
import { editableOrderTypes, moduleOptions } from "./order-type-exception.constants";

interface AddOrderTypeExceptionProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}


const emptyForm = {
  company: "",
  division: "",
  counter: "",
  state: "",
  stockist_code: "",
  item_code: "",
  module: "",
  order_type: "",
  reference_number: "",
};

// Autocomplete gives an object when an option is picked; anything else means "All"
const pick = (value: any, key: string) => (value && typeof value === "object" ? value[key] ?? null : null);
const toNullable = (value: any) => (value === null || value === undefined || String(value).trim() === "" ? null : String(value).trim());
const requiredOption = (message: string) =>
  yup.lazy((value) => (typeof value === "object" ? yup.object() : yup.string()).required(message));

const schema = yup.object().shape({
  company: requiredOption("Company is required"),
  module: requiredOption("Module is required"),
  order_type: requiredOption("Order Type is required"),
  counter: yup.mixed().test("counter-without-state", "Counter can't be combined with State", function (value) {
    return !pick(value, "counter_code") || !pick(this.parent.state, "state_code");
  }),
  reference_number: yup.string().trim().nullable().max(150, "Remarks must not exceed 150 characters"),
});

const AddOrderTypeException = ({ isOpen, onClose, onSuccess }: AddOrderTypeExceptionProps) => {
  const { toast } = useToast();
  const { poFiltersData } = useSelector((state: any) => state.po);
  const { fetchState } = usePoHandlers();
  const [divisionList, setDivisionList] = useState<any[]>([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<number | string | null>(null);
  const [selectedDivisionCode, setSelectedDivisionCode] = useState<number | string | null>(null); // M10-16121
  const [loading, setLoading] = useState(false);

  const form = useForm<any>({ resolver: yupResolver(schema), defaultValues: emptyForm, mode: "onBlur" });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(emptyForm as any);
    setDivisionList([]);
    setSelectedCompanyCode(null);
    setSelectedDivisionCode(null);
    if (!poFiltersData?.state?.length) fetchState?.();
  }, [isOpen]);

  const handleAutocomplete = (value: any, keyofForm?: string) => {
    // M10-16121: counters are filtered by division, so a picked counter may no longer apply
    if (keyofForm === "division") {
      const divisionCode = value?.company_divisioncode ?? null;
      if (String(divisionCode ?? "") !== String(selectedDivisionCode ?? "")) form.setValue("counter", "");
      setSelectedDivisionCode(divisionCode);
      return;
    }
    if (keyofForm !== "company") return;

    // Division and counter belong to the company, so a different company clears them
    if (String(value?.code ?? "") !== String(selectedCompanyCode ?? "")) {
      form.setValue("division", "");
      form.setValue("counter", "");
      setSelectedDivisionCode(null);
    }
    setSelectedCompanyCode(value?.code ?? null);
    setDivisionList([]);
    if (!value?.code) return;
    queryDivisionDetails(`/details/?fields=division_name&company_code=${value.code}`)
      .then((res: any) => setDivisionList(res?.data || []))
      .catch((err) => console.log(err));
  };

  // One option per counter location linked to the company (and division, when picked): "<counter_code> - <location_code>"
  const searchCounters = (query?: string) =>
    selectedCompanyCode
      ? getOrderCapFreezeCounters(selectedCompanyCode, query || "", null, selectedDivisionCode).then((res) => res?.data?.data || [])
      : Promise.resolve([]);

  const formFields = [
    {
      id: "company",
      type: "autocomplete",
      label: "Company",
      keyofForm: "company",
      displayKey: "name",
      suggestionList: poFiltersData?.company || [],
    },
    {
      id: "division",
      type: "autocomplete",
      label: "Division",
      keyofForm: "division",
      displayKey: "division_name",
      suggestionList: divisionList,
    },
    {
      id: "counter",
      type: "autocomplete",
      label: "Counter Code",
      keyofForm: "counter",
      displayKey: "counter_location_label",
      isAsyncQuery: true,
      asyncListFunction: searchCounters,
    },
    {
      id: "state",
      type: "autocomplete",
      label: "State",
      keyofForm: "state",
      displayKey: "state_name",
      suggestionList: poFiltersData?.state || [],
    },
    { id: "stockist_code", type: "text", label: "Company Stockist Code", placeholder: "Enter Company Stockist Code", keyofForm: "stockist_code" },
    { id: "item_code", type: "text", label: "Item Code", placeholder: "Enter Item Code", keyofForm: "item_code" },
    {
      id: "module",
      type: "autocomplete",
      label: "Module",
      keyofForm: "module",
      displayKey: "label",
      suggestionList: moduleOptions,
    },
    {
      id: "order_type",
      type: "autocomplete",
      label: "Order Type",
      keyofForm: "order_type",
      displayKey: "label",
      suggestionList: editableOrderTypes,
    },
    { id: "reference_number", type: "text", label: "Remarks", placeholder: "Enter Remarks", keyofForm: "reference_number" },
  ];

  const handleSubmit = async (values: any) => {
    const companyCode = pick(values.company, "code");
    const divisionCode = pick(values.division, "company_divisioncode");
    const counterCode = toNullable(pick(values.counter, "counter_code"));
    const locationCode = pick(values.counter, "location_code");
    const stateName = pick(values.state, "state_code") ? pick(values.state, "state_name") : null;
    const stockistCode = toNullable(values.stockist_code);
    const itemCode = toNullable(values.item_code);

    if (!divisionCode && !counterCode && !stockistCode && !itemCode && !stateName) {
      toast({ description: "Select at least one of Division, Counter, Stockist, Item or State.", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);

      // Same corp validation bulk upload uses: checks the masters and builds the display metadata.
      const validation = await getOrderTypeExceptionValidation([
        {
          companyCode,
          companyDivisionCode: divisionCode,
          counterCode,
          locationCode: locationCode != null ? Number(locationCode) : null,
          companyStockistCode: stockistCode,
          itemCode,
          state: stateName,
          orderType: pick(values.order_type, "value"),
          module: pick(values.module, "name"),
          activeStatus: "Yes",
        },
      ]);

      const { success, data, errors, message } = (validation as any)?.data || {};
      const validRow = data?.[0];
      if (!success || !validRow) {
        const rowError = errors?.find((e: any) => e?.Message?.trim())?.Message;
        toast({ description: rowError || message || "Validation failed", variant: "destructive" });
        return;
      }

      await addOrderTypeException(
        {
          company_code: validRow.companyCode,
          company_division_code: validRow.companyDivisionCode,
          counter_code: validRow.counterCode,
          location_code: validRow.locationCode,
          company_stockist_code: validRow.companyStockistCode,
          item_code: validRow.itemCode,
          order_type: validRow.orderType,
          cndn_remarks_code: validRow.cndn_remarks_code,
          module_code: pick(values.module, "value"),
          activeStatus: "Yes",
          reference_number: toNullable(values.reference_number),
          metadata: validRow.metadata,
        },
        companyCode
      );

      toast({ description: "Order type exception added successfully." });
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error adding order type exception:", err);
      toast({
        description: err?.response?.data?.message || err?.message || "Failed to add order type exception.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} isHeader={true} title="Add Order Type Exception" onCloseModal={onClose}>
      <div className="w-[36rem] max-w-[90vw]">
        <GenericForm
          formInstance={form}
          formFields={formFields}
          schema={schema}
          defaultValues={emptyForm}
          isFormChangedCheck={false}
          onAutocompleteChange={handleAutocomplete}
          onFormSubmit={handleSubmit}
        >
          <footer className="flex items-center justify-end gap-3 p-5 pt-0">
            <Button className="border" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="text-white bg-primary" isLoading={loading} loaderSize="small">
              Add
            </Button>
          </footer>
        </GenericForm>
      </div>
    </Modal>
  );
};

export default AddOrderTypeException;
// ORDER-TYPE-EXCEPTION-CRUD
